import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import RefundDestinationPicker, { PickableDestination } from './RefundDestinationPicker';
import { RefundDestination, RefundOutcome, describeRefundOutcome } from '../../lib/refunds';

/**
 * The one refund dialog. Shows what is owed, lets staff pick where it goes
 * (card / wallet / cash / e-transfer, with the card cap up front), then hands
 * the choice to the caller. The caller performs the operation — deposit
 * pickup, wallet payout, invoice void — and returns where the money landed.
 */
export interface RefundSheetProps {
  open: boolean;
  onClose: () => void;
  businessId: string;
  customerId?: string | null;
  invoiceId?: string | null;
  bookingId?: string | null;
  amountCents: number;
  title?: string;
  /** One line of context: "Deposit refund — 10 × 5 Gallon Jug". */
  description?: string;
  destinations?: RefundDestination[];
  defaultDestination?: RefundDestination;
  confirmLabel?: string;
  /** Do the thing. Resolve with the outcome to show a receipt; reject to show the error. */
  onConfirm: (choice: { destination: RefundDestination; reference: string | null }) => Promise<RefundOutcome | null | void>;
  /** Fired after the receipt is dismissed (or immediately when no outcome is returned). */
  onDone?: (outcome: RefundOutcome | null) => void;
}

const fmt = (c: number) => `$${(c / 100).toFixed(2)}`;

export default function RefundSheet({
  open,
  onClose,
  businessId,
  customerId,
  invoiceId,
  bookingId,
  amountCents,
  title,
  description,
  destinations = ['card', 'wallet', 'cash', 'e_transfer'],
  defaultDestination,
  confirmLabel,
  onConfirm,
  onDone,
}: RefundSheetProps) {
  const [destination, setDestination] = useState<PickableDestination>(defaultDestination ?? destinations[0]);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<RefundOutcome | null>(null);

  if (!open) return null;

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await onConfirm({
        destination: destination as RefundDestination,
        reference: reference.trim() || null,
      });
      if (result) {
        setOutcome(result);
      } else {
        onDone?.(null);
        onClose();
      }
    } catch (e: any) {
      setError(e?.message || 'Refund failed');
    } finally {
      setBusy(false);
    }
  };

  const finish = () => {
    onDone?.(outcome);
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      // Above every host: customer profile (9,999,999), carts, side panels.
      style={{ zIndex: 2147483000, backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
      onClick={busy ? undefined : finish}
    >
      <div
        className="w-full max-w-md rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 pt-4 pb-3">
          <div className="min-w-0">
            <h3 className="text-[16px] font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
              {title ?? `Refund ${fmt(amountCents)}`}
            </h3>
            {description && <p className="mt-0.5 text-[12px] text-gray-500">{description}</p>}
          </div>
          <button
            type="button"
            onClick={finish}
            disabled={busy}
            aria-label="Close"
            className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          {outcome ? (
            <div className="space-y-3 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
              <p className="text-[15px] font-semibold text-gray-900">Refund recorded</p>
              <p className="text-[13px] text-gray-600">{describeRefundOutcome(outcome)}</p>
              <button
                type="button"
                onClick={finish}
                className="mt-1 w-full rounded-xl bg-gray-900 py-2.5 text-[14px] font-semibold text-white hover:bg-black"
              >
                Done
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-[13px] font-semibold text-gray-700">Amount owed</span>
                <span className="text-[22px] font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>{fmt(amountCents)}</span>
              </div>
              <RefundDestinationPicker
                businessId={businessId}
                customerId={customerId}
                invoiceId={invoiceId}
                bookingId={bookingId}
                amountCents={amountCents}
                value={destination}
                onChange={setDestination}
                destinations={destinations}
                reference={reference}
                onReferenceChange={setReference}
              />
              {error && (
                <p className="flex items-start gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-medium text-red-700">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {error}
                </p>
              )}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={finish}
                  disabled={busy}
                  className="flex-1 rounded-xl border border-gray-200 py-2.5 text-[14px] font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={busy || amountCents <= 0}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 py-2.5 text-[14px] font-semibold text-white hover:bg-emerald-700 disabled:bg-gray-300"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                  {confirmLabel ?? `Refund ${fmt(amountCents)}`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
